import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import './ChatMarkdownBody.css';

function decodeNewlines(text: string): string {
  return text.replace(/\\n/g, '\n');
}

/** Escape list markers at line start so plain lines like "- item" stay literal when not meant as lists. */
function escapeMarkdownListMarkers(text: string): string {
  return text.replace(/^(\s{0,3})([+\-*])(?=\s|$)/gm, '$1\\$2');
}

/** CommonMark collapses single newlines; two trailing spaces + newline = hard break (skip inside fenced code). */
function preserveSingleLineBreaks(text: string): string {
  if (text.includes('```')) return text;
  return text.replace(/([^\n])\n(?!\n)/g, '$1  \n');
}

const markdownComponents: Components = {
  h1: ({ children }) => <h1 className="markdown-h1">{children}</h1>,
  h2: ({ children }) => <h2 className="markdown-h2">{children}</h2>,
  h3: ({ children }) => <h3 className="markdown-h3">{children}</h3>,
  h4: ({ children }) => <h4 className="markdown-h4">{children}</h4>,
  h5: ({ children }) => <h5 className="markdown-h5">{children}</h5>,
  h6: ({ children }) => <h6 className="markdown-h6">{children}</h6>,
  p: ({ children }) => <p className="markdown-p">{children}</p>,
  ul: ({ children, className }) => {
    const isTaskList = className?.includes('contains-task-list');
    return <ul className={`markdown-ul ${isTaskList ? 'markdown-task-list' : ''}`}>{children}</ul>;
  },
  ol: ({ children }) => <ol className="markdown-ol">{children}</ol>,
  li: ({ children, className }) => {
    const isTaskItem = className?.includes('task-list-item');
    if (isTaskItem) {
      const childrenArray = React.Children.toArray(children);
      const checkbox = childrenArray[0] as React.ReactElement;
      const text = childrenArray.slice(1);
      const isChecked = checkbox?.props?.checked;
      return (
        <li className="markdown-task-list-item">
          <input type="checkbox" checked={isChecked} readOnly className="markdown-checkbox" />
          <span className={isChecked ? 'markdown-task-completed' : ''}>{text}</span>
        </li>
      );
    }
    return <li className="markdown-li">{children}</li>;
  },
  blockquote: ({ children }) => <blockquote className="markdown-blockquote">{children}</blockquote>,
  code: ({ className, children, ...props }) => {
    const match = /language-(\w+)/.exec(className || '');
    const isInline = !match;
    return !isInline ? (
      <pre className="markdown-pre" data-language={match?.[1]}>
        <code className={`markdown-code language-${match?.[1]}`} {...props}>
          {children}
        </code>
      </pre>
    ) : (
      <code className="markdown-inline-code" {...props}>
        {children}
      </code>
    );
  },
  table: ({ children }) => <table className="markdown-table">{children}</table>,
  thead: ({ children }) => <thead className="markdown-thead">{children}</thead>,
  tbody: ({ children }) => <tbody className="markdown-tbody">{children}</tbody>,
  tr: ({ children }) => <tr className="markdown-tr">{children}</tr>,
  th: ({ children }) => <th className="markdown-th">{children}</th>,
  td: ({ children }) => <td className="markdown-td">{children}</td>,
  a: ({ children, href }) => (
    <a href={href} className="markdown-link" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  strong: ({ children }) => <strong className="markdown-strong">{children}</strong>,
  em: ({ children }) => <em className="markdown-em">{children}</em>,
  hr: () => <hr className="markdown-hr" />,
  img: ({ src, alt }) => <img src={src} alt={typeof alt === 'string' ? alt : ''} className="markdown-img" />,
  del: ({ children }) => <del className="markdown-del">{children}</del>,
  ins: ({ children }) => <ins className="markdown-ins">{children}</ins>,
};

export interface ChatMarkdownBodyProps {
  children: string;
  className?: string;
}

/**
 * Renders assistant/user-style markdown (GFM + highlighted fenced code) for chat-like surfaces.
 */
const ChatMarkdownBody: React.FC<ChatMarkdownBodyProps> = React.memo(({ children, className }) => {
  const prepared = useMemo(
    () => preserveSingleLineBreaks(escapeMarkdownListMarkers(decodeNewlines(children ?? ''))),
    [children]
  );
  return (
    <div className={['chat-viewer-markdown', className].filter(Boolean).join(' ')}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]} components={markdownComponents}>
        {prepared}
      </ReactMarkdown>
    </div>
  );
});

ChatMarkdownBody.displayName = 'ChatMarkdownBody';

export default ChatMarkdownBody;
